/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : ViewImage.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { HandleApiActions } from "../../../redux/actions";
import testCasesActions from "../../../redux/actions/testCasesActions";

import {
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

function ViewImage({
  open,
  onClose,
  dialogTitle,
  selectedTestCaseData,
  setSelectedTestCaseData,
}) {
  const dispatch = useDispatch();

  // Files
  const [fileDataList, setFileDataList] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchFileBlob = async (fileName) => {
    try {
      const resp = await dispatch(
        HandleApiActions({
          ...testCasesActions.get_files,
          params: {
            filename: fileName,
          },
          is_get_file: true,
        }),
      );
      const url = window.URL.createObjectURL(new Blob([resp]));

      return { fileName, url, error: false };
    } catch (error) {
      console.error("Error fetching file:", fileName, error);
      return { fileName, url: null, error: true };
    }
  };

  useEffect(() => {
    const loadFiles = async () => {
      setLoading(true);
      const results = await Promise.all(
        selectedTestCaseData?.artefact_link?.map((name) => fetchFileBlob(name)),
      );
      console.log("testingImage", results);
      setFileDataList(results);
      setLoading(false);
    };

    if (open && selectedTestCaseData) {
      loadFiles();
    }
  }, [open, selectedTestCaseData]);
  return (
    <Dialog
      open={open}
      onClose={() => {
        onClose();
        setSelectedTestCaseData();
      }}
      PaperProps={{
        sx: { backgroundColor: "transparent", boxShadow: "none" },
      }}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          backgroundColor: "transparent",
        }}
      >
        <IconButton onClick={onClose} size="small" sx={{ color: "white" }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ backgroundColor: "transparent" }}>
        {loading ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            height={700}
          >
            <CircularProgress />
          </Box>
        ) : (
          <Box display="flex" flexDirection="column" gap={4}>
            {fileDataList.map(({ fileName, url, error }, index) => (
              <Box
                key={index}
                borderRadius={2}
                p={2}
                overflow="hidden"
                sx={{ backgroundColor: "transparent" }}
              >
                <Typography
                  variant="subtitle2"
                  gutterBottom
                  sx={{ wordBreak: "break-word", fontWeight: 500 }}
                >
                  {fileName}
                </Typography>
                {error || !url ? (
                  <Typography color="error">Unable to load image</Typography>
                ) : (
                  <Box
                    component="img"
                    src={url}
                    alt={fileName}
                    sx={{
                      p: 2,
                      width: "100%",
                      maxHeight: 400,
                      objectFit: "contain",
                      borderRadius: 1,
                      border: "1px solid #ddd",
                      backgroundColor: "#f9f9f9",
                    }}
                  />
                )}
              </Box>
            ))}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default ViewImage;
